import { tpoCertificationApi } from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

export const TPO_CERTIFICATION_ACTION_TYPE = createNamedActions('tpoCertification', {
  GET_SUB_COMPANY_READ_REQUEST: 'GET_SUB_COMPANY_READ_REQUEST',
  GET_ACTIVE: 'GET_ACTIVE',
  GET_ACTIVE_PAGE: 'GET_ACTIVE_PAGE',
  GET_SIGNUP_CONFIG: 'GET_SIGNUP_CONFIG',
  GET_EVALUATED_STEPS: 'GET_EVALUATED_STEPS',
  GET_EVALUATED_STEPS_REQUEST: 'GET_EVALUATED_STEPS_REQUEST',
  GET_HISTORY: 'GET_HISTORY',
  GET_CAN_START_NEW_CYCLE: 'GET_CAN_START_NEW_CYCLE',
  START_NEW_CYCLE: 'START_NEW_CYCLE',
  GET_PROFILE: 'GET_PROFILE',
  GET_DOCUMENTS: 'GET_DOCUMENTS',
  UPDATE_PROFILE_SECTION: 'UPDATE_PROFILE_SECTION',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  REVOKE: 'REVOKE',
  RESET_STATE: 'RESET_STATE',
});

const dispatchSubCompanyReadRequest = (dispatch, params) => {
  const meta = { subCompanyId: params?.subCompanyId ?? null };

  dispatch({ type: TPO_CERTIFICATION_ACTION_TYPE.GET_SUB_COMPANY_READ_REQUEST, meta });

  return meta;
};

// Flat TpoCertificationDto — the header binds directly to this.
export const getTpoCertificationActiveAction = catchAsyncActionError(params => async dispatch => {
  const meta = dispatchSubCompanyReadRequest(dispatch, params);
  const { data } = await tpoCertificationApi.getActive(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_ACTIVE,
    payload: data,
    meta,
  });
});

// getActivePage returns GetActiveTpoCertificationResponse { activeCertification: TpoCertificationPageDto | null }.
// Unwrap the envelope here so the store holds ONE canonical flat TpoCertificationPageDto
// (pageStatus / certifiedUntil / cycleType) that every consumer reads the same way.
export const getTpoCertificationActivePageAction = catchAsyncActionError(
  params => async dispatch => {
    const meta = dispatchSubCompanyReadRequest(dispatch, params);
    const { data } = await tpoCertificationApi.getActivePage(params);

    return dispatch({
      type: TPO_CERTIFICATION_ACTION_TYPE.GET_ACTIVE_PAGE,
      payload: data?.activeCertification ?? null,
      meta,
    });
  },
);

export const getTpoCertificationHistoryAction = catchAsyncActionError(params => async dispatch => {
  const meta = dispatchSubCompanyReadRequest(dispatch, params);
  const { data } = await tpoCertificationApi.getHistory(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_HISTORY,
    payload: data,
    meta,
  });
});

export const getTpoCertificationCanStartNewCycleAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await tpoCertificationApi.getCanStartNewCycle(params);

    return dispatch({
      type: TPO_CERTIFICATION_ACTION_TYPE.GET_CAN_START_NEW_CYCLE,
      payload: data,
    });
  },
);

export const startTpoCertificationNewCycleAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await tpoCertificationApi.startNewCycle(params);

    return dispatch({
      type: TPO_CERTIFICATION_ACTION_TYPE.START_NEW_CYCLE,
      payload: data,
    });
  },
);

export const getTpoCertificationProfileAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await tpoCertificationApi.getProfile(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_PROFILE,
    payload: data,
  });
});

// Enhancement-only variant of getTpoCertificationProfileAction for a TPO user's OWN Company Manager
// surface: a 403 there must neither trip the global forbidden page-reload (skipForbidden), toast, nor
// re-reject — so this swallows to a null payload, mirroring getTpoCertificationEvaluatedStepsAction.
export const getTpoCertificationProfileQuietAction = params => async dispatch => {
  const profile = await tpoCertificationApi
    .getProfile({ ...params, skipForbidden: true })
    .then(response => response.data || null)
    .catch(() => null);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_PROFILE,
    payload: profile,
  });
};

// Every document uploaded against the certification (TpoDocumentDto[]), keyed to its slot id.
// Enhancement-only on the broker's own Company Manager surface, so a failure resolves to null
// rather than re-rejecting: the read-only view then says the documents could not be loaded
// instead of blanking the whole drawer. Null and [] are deliberately distinct — [] means the
// cycle genuinely has no uploads. A body that is not a list is not an empty cycle either: it is a
// read that cannot be believed, so it resolves to null rather than reaching the view as data it
// would try to iterate.
export const getTpoCertificationDocumentsQuietAction = params => async dispatch => {
  const documents = await tpoCertificationApi
    .getCertificationDocuments({ ...params, skipForbidden: true })
    .then(response => (Array.isArray(response.data) ? response.data : null))
    .catch(() => null);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_DOCUMENTS,
    payload: documents,
  });
};

// Provenance only ({ tpoSignupConfigurationId, name, isDefault }) for the signup config the
// SubCompany was created under — it names the config on the status strip. A failure to read it
// means "no provenance to show", so this deliberately swallows rather than going through
// catchAsyncActionError, which would re-reject into an error toast. A 204 yields no body.
export const getTpoCertificationSignupConfigAction = params => async dispatch => {
  const config = await tpoCertificationApi
    .getSignupConfig(params)
    .then(response => response.data || null)
    .catch(() => null);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_SIGNUP_CONFIG,
    payload: config,
  });
};

// The steps this cert cycle actually ran, with every `conditionExpression` evaluated SERVER-side
// against the persisted profile. The browser cannot run the expression engine, so this is the only
// faithful answer to "which sections and questionnaires was this broker asked". A failure leaves
// the payload null and the tab falls back to its full, unfiltered section list.
export const getTpoCertificationEvaluatedStepsAction = params => async dispatch => {
  const meta = { certificationId: params.certificationId ?? null };

  dispatch({ type: TPO_CERTIFICATION_ACTION_TYPE.GET_EVALUATED_STEPS_REQUEST, meta });

  const steps = await tpoCertificationApi
    .getEvaluatedSteps(params)
    .then(response => response.data || null)
    .catch(() => null);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.GET_EVALUATED_STEPS,
    payload: steps,
    meta,
  });
};

export const updateTpoCertificationProfileSectionAction = catchAsyncActionError(
  params => async dispatch => {
    const { data } = await tpoCertificationApi.updateProfileSection(params);

    return dispatch({
      type: TPO_CERTIFICATION_ACTION_TYPE.UPDATE_PROFILE_SECTION,
      payload: { section: params.section, data },
    });
  },
);

export const approveTpoCertificationAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await tpoCertificationApi.approve(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.APPROVE,
    payload: data,
  });
});

export const rejectTpoCertificationAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await tpoCertificationApi.reject(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.REJECT,
    payload: data,
  });
});

export const revokeTpoCertificationAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await tpoCertificationApi.revoke(params);

  return dispatch({
    type: TPO_CERTIFICATION_ACTION_TYPE.REVOKE,
    payload: data,
  });
});

export const resetTpoCertificationAction = () => ({
  type: TPO_CERTIFICATION_ACTION_TYPE.RESET_STATE,
});
